import { Link, spacing, Text } from '@scality/core-ui';
import type { JSONSchema7 } from 'json-schema';
import { validate } from 'jsonschema';
import Markdown from 'marked-react';
import React, { type ReactNode } from 'react';
import styled from 'styled-components';
import { useFocusWidget } from '../../dashboard/WidgetFocusProvider';
import { PanelBoundary, type PanelPlacement } from '../../dualPanel';
import CODEBLOCK_LANGS from '../../gen/codeblock-langs.json';
import { useTrackError } from '../../hooks/useTrackError';
import { hasInstanceMarker, parseInstanceMarker } from '../../portal/instanceMarker';
import { AlertsList } from '../ai-tools/AlertsList/AlertsList';
import { BarChart } from '../ai-tools/BarChart/BarChart';
import { ErrorWidget } from '../ai-tools/ErrorWidget/ErrorWidget';
import { FollowUpQuestions } from '../ai-tools/FollowUpQuestions/FollowUpQuestions';
import { Heatmap } from '../ai-tools/Heatmap/Heatmap';
import { HistChart } from '../ai-tools/HistChart/HistChart';
import { LineChart } from '../ai-tools/LineChart/LineChart';
import { MetricCards } from '../ai-tools/MetricCards/MetricCards';
import { NestedProportionBar } from '../ai-tools/NestedProportionBar/NestedProportionBar';
import { VitalCard } from '../ai-tools/VitalCard/VitalCard';
import { VitalsDashboard } from '../ai-tools/VitalsDashboard/VitalsDashboard';
import { ZendeskTicket } from '../ai-tools/ZendeskTicket/ZendeskTicket';
import { InstanceBadge } from '../InstanceBadge';
import { InstanceMarkerText } from '../InstanceMarkerText';
import { CodeBlock } from './CodeBlock';
import { Mermaid } from './Mermaid';
import { StyledTable, StyledTbody, StyledTd, StyledTh, StyledThead, StyledTr } from './Table';
import { ThinkingLoader } from './ThinkingLoader';

const IS_DEV = process.env.NODE_ENV === 'development';

/** `widget://<id>` links scroll to + highlight the referenced dashboard widget. */
const WIDGET_LINK_PREFIX = 'widget://';

/** Only these links are clickable; anything else is rendered as unsafe (`DangerousLink`). */
const ALLOWED_EMAILS = ['support@scality.com'];
const ALLOWED_WEB_DOMAINS = [
  'scality.com',
  'scality.io',
  'scality.net',
  'artesca.com',
  'scality-support.zendesk.com',
  'google.com',
  ...(IS_DEV ? ['localhost', '127.0.0.1'] : []),
];

/** Base for parsing relative hrefs when there is no `window` (SSR, tests). */
const FALLBACK_URL_BASE = 'https://app.local';

const InlineCode = styled.code`
  font-family: 'Fira Code', monospace;
  padding: 0.1rem 0.2rem;
  border-radius: ${spacing.r4};
  background-color: ${(props) => props.theme.infoPrimary};
  color: ${(props) => props.theme.textReverse};
  overflow-wrap: break-word;
  // Monospace fonts render larger than Lato at the same size; scale down to compensate
  font-size: 0.875rem;
`;

// `widget://` links have no href to navigate to — an <a> without href is not keyboard
// focusable, so render a real button and keep the link look (focus + Enter/Space for free).
const WidgetLink = styled(Link).attrs({ as: 'button', type: 'button' })`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${(props) => props.theme.textLink};
  text-align: left;
`;

const DangerousLink: React.FC<{ href: string; text: ReactNode }> = ({ href, text }) => (
  <Text color="statusWarning">
    {text}{' '}
    <Text isEmphazed>{href} is made unclickable as it may be malicious, double check the link before opening it</Text>
  </Text>
);

export type AIToolComponent<Params = {}> = React.FC<{ params: Params; isLastResponse?: boolean }> & {
  description: string;
  schema: JSONSchema7;
  displayInPanel?: boolean;
  panelPlacement?: PanelPlacement;
};

export const GUARDIAN_MARKED_TOOL_COMPONENTS: Record<string, AIToolComponent<any>> = {
  AlertsList,
  BarChart,
  FollowUpQuestions,
  Heatmap,
  HistChart,
  LineChart,
  MetricCards,
  NestedProportionBar,
  VitalCard,
  VitalsDashboard,
  ZendeskTicket,
};

export const GUARDIAN_MARKED_COMPONENTS: Record<string, AIToolComponent<any>> = {
  ...GUARDIAN_MARKED_TOOL_COMPONENTS,
  Error: ErrorWidget,
  ThinkingLoader,
};

const MarkdownWrapper = styled.div`
  color: ${(props) => props.theme.textPrimary};
  
  /* Vertical rhythm typography */
  line-height: 1.3;
  
  /* Paragraph spacing - greater than line spacing */
  p {
    margin-bottom: 1.2rem;
    line-height: 1.3;
  }
  
  /* List item spacing */
  li {
    margin-bottom: 0.3rem;
    line-height: 1.3;
  }
  
  /* Paragraph introducing a list - visually grouped */
  p + ul,
  p + ol {
    margin-top: -0.5rem;
  }
`;

type GuardianMarkdownProps = {
  md: string;
  fallback?: React.ReactNode;
  isLastResponse?: boolean;
  /** Identifies the source message so extracted panel widgets keep conversation order. */
  messageId?: string;
};

export const GuardianMarkdown: React.FC<GuardianMarkdownProps> = ({
  md,
  isLastResponse,
  messageId,
  fallback = `loading...`,
}) => {
  // Our overridden renderers return elements without a key, triggering React's
  // "unique key prop" warning. Hand out unique keys in parse order.
  let keySeq = 0;
  const nextKey = () => `gmd-${keySeq++}`;
  const focusWidget = useFocusWidget();

  return (
    <React.Suspense fallback={fallback}>
      <MarkdownWrapper>
        <Markdown
          value={md}
          renderer={{
            code: (code: string, lang?: string) => {
              const key = nextKey();

              if (!lang) {
                return <CodeBlock key={key} code={code} language="text" />;
              }

              if (lang === 'mermaid') {
                return <Mermaid key={key} code={code} />;
              }

              if (CODEBLOCK_LANGS.includes(lang)) {
                return <CodeBlock key={key} code={code} language={lang} />;
              }

              const componentName = (lang ?? '').replace(/^json\+/, '').trim();
              const Component = GUARDIAN_MARKED_COMPONENTS[componentName];
              if (!Component) {
                return <GuardianErrorComponent key={key} message={`No component found for language: ${lang}`} />;
              }

              try {
                const params = JSON.parse(code);
                const validationResult = validate(params, Component.schema);
                if (!validationResult.valid) {
                  throw new Error(
                    `Invalid props for component ${componentName}: ${validationResult.errors
                      .map((validationError) => validationError.stack)
                      .join('; ')}`,
                  );
                }

                return (
                  <PanelBoundary
                    key={key}
                    component={Component}
                    componentName={componentName}
                    params={params}
                    messageId={messageId}
                    isLastResponse={isLastResponse}
                  />
                );
              } catch (error) {
                return (
                  <GuardianErrorComponent
                    key={key}
                    error={error}
                    message={`Error rendering the ${componentName} component`}
                  />
                );
              }
            },
            table: (children: React.ReactNode[]) => {
              return <StyledTable key={nextKey()}>{children}</StyledTable>;
            },
            tableHeader: (children: React.ReactNode) => {
              return <StyledThead key={nextKey()}>{children}</StyledThead>;
            },
            tableBody: (children: React.ReactNode[]) => {
              return <StyledTbody key={nextKey()}>{children}</StyledTbody>;
            },
            tableRow: (children: React.ReactNode[]) => {
              return <StyledTr key={nextKey()}>{children}</StyledTr>;
            },
            tableCell: (
              children: React.ReactNode[],
              flags: { header?: boolean; align?: 'center' | 'left' | 'right' | null },
            ) => {
              const Tag = flags.header ? StyledTh : StyledTd;
              const style = flags.align ? { textAlign: flags.align } : undefined;
              return (
                <Tag key={nextKey()} style={style}>
                  {children}
                </Tag>
              );
            },
            codespan: (code: ReactNode) => {
              // `(id)<instanceId>` names a deployment in prose. The prefix is stripped
              // whether or not the feature flag is on: with it off the badge simply reads
              // the raw id, so marker syntax never reaches a user either way.
              const instanceId = typeof code === 'string' ? parseInstanceMarker(code) : null;
              if (instanceId) {
                return <InstanceBadge key={nextKey()} id={instanceId} />;
              }
              return <InlineCode key={nextKey()}>{code}</InlineCode>;
            },
            text: (text: ReactNode) => {
              if (typeof text === 'string' && hasInstanceMarker(text)) {
                return <InstanceMarkerText key={nextKey()} text={text} />;
              }
              return text;
            },
            paragraph: (children: ReactNode) => {
              return <p key={nextKey()}>{children}</p>;
            },
            link: (href: string, text: ReactNode) => {
              const key = nextKey();
              try {
                if (href.startsWith(WIDGET_LINK_PREFIX)) {
                  const widgetId = decodeURIComponent(href.slice(WIDGET_LINK_PREFIX.length));
                  return (
                    <WidgetLink key={key} onClick={() => focusWidget(widgetId)}>
                      {text}
                    </WidgetLink>
                  );
                }
                const urlObj = new URL(
                  href,
                  typeof window !== 'undefined' ? window.location.origin : FALLBACK_URL_BASE,
                );
                if (urlObj.protocol === 'mailto:') {
                  const email = decodeURIComponent(urlObj.pathname).split('?')[0]?.trim().toLowerCase();
                  if (email && ALLOWED_EMAILS.includes(email)) {
                    return (
                      <Link key={key} href={href}>
                        {text}
                      </Link>
                    );
                  }
                }
                if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
                  const domain = urlObj.hostname;
                  const isAllowed = ALLOWED_WEB_DOMAINS.some(
                    (allowedDomain) => domain === allowedDomain || domain?.endsWith(`.${allowedDomain}`),
                  );
                  if (isAllowed) {
                    return (
                      <Link key={key} href={href} target="_blank" rel="noopener noreferrer">
                        {text}
                      </Link>
                    );
                  }
                } else {
                  return <DangerousLink key={key} href={href} text={text} />;
                }
              } catch (_e) {
                return <DangerousLink key={key} href={href} text={text} />;
              }
              return <DangerousLink key={key} href={href} text={text} />;
            },
            image: () => {
              //Disabling Images rendering for security reasons
              //Images are not supported by Guardian for now
              return <React.Fragment key={nextKey()} />;
            },
          }}
        />
      </MarkdownWrapper>
    </React.Suspense>
  );
};

export const GuardianErrorComponent: React.FC<{ error?: unknown; message?: string }> = ({ error, message }) => {
  useTrackError(error ?? message, message);
  return <div style={{ color: 'red', fontWeight: 'bold' }}>An error occurred while rendering this component.</div>;
};
